import React from "react";
import { Link } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import MailOutlineRounded from "@mui/icons-material/MailOutlineRounded";
import ConfirmationNumberOutlined from "@mui/icons-material/ConfirmationNumberOutlined";
import { colors } from "../colors";
import { PageContainer } from "../PageContainer";
import {
  DISCORD_INVITE_URL,
  SUPPORT_EMAIL,
} from "../../../public-site/siteData";
import {
  DARK_HERO_HEADER_HEIGHT_PX,
  DARK_HERO_HEADER_XL_HEIGHT_PX,
} from "../../publicHeaderLayout";

const PAGE_BG = "#080808";
const LAST_UPDATED = "22 August 2024";
const DANGER = colors.state.danger.main;

const TOC_ITEMS = [
  { id: "s1", num: "1.", label: "Acceptance of terms" },
  { id: "s2", num: "2.", label: "Eligibility" },
  { id: "s3", num: "3.", label: "Account registration" },
  { id: "s4", num: "4.", label: "Use of the service" },
  { id: "s5", num: "5.", label: "Violations & actions" },
  { id: "s6", num: "6.", label: "Intellectual property" },
  { id: "s7", num: "7.", label: "User-generated content" },
  { id: "s8", num: "8.", label: "Payment & refunds" },
  { id: "s9", num: "9.", label: "Disclaimers" },
  { id: "s10", num: "10.", label: "Service changes" },
  { id: "s11", num: "11.", label: "Terms changes" },
  { id: "s12", num: "12.", label: "Governing law" },
  { id: "s13", num: "13.", label: "Contact us" },
];

const PROHIBITED = [
  "Cheating, hacking, or using unauthorised third-party software that provides an unfair in-game advantage",
  "Harassing, bullying, or engaging in toxic behaviour toward other players",
  "Exploiting bugs, glitches, or unintended mechanics in the service",
  "Impersonating another user or attempting to gain unauthorised access to accounts",
  "Intentionally abandoning matches, going AFK, or griefing teammates",
  "Filing false or bad-faith reports against other players",
];

const STRIKES = [
  {
    variant: "one",
    label: "Strike 1",
    title: "Cooldown",
    body: "A temporary period during which you cannot participate in matchmaking. You will be provided with a reason for each cooldown issued.",
    accent: colors.primary.main,
    labelColor: colors.primary.main,
  },
  {
    variant: "two",
    label: "Strike 2",
    title: "Extended cooldown",
    body: "A longer suspension from matchmaking. At this stage, the account is flagged for closer monitoring by the moderation team.",
    accent: "rgba(224,147,74,0.8)",
    labelColor: "rgba(224,147,74,0.8)",
  },
  {
    variant: "three",
    label: "Strike 3",
    title: "Permanent ban",
    body: "After three strikes, your account is permanently banned from Ouro. This decision is final and not subject to appeal.",
    accent: DANGER,
    labelColor: DANGER,
  },
];

const sectionLabelSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "10px",
  fontWeight: 700,
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  color: colors.text.muted,
  mb: 1,
};

const sectionTitleSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "22px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: colors.text.primary,
  mb: 2.75,
  lineHeight: 1.1,
};

const bodySx = {
  fontSize: "15px",
  color: colors.text.secondary,
  lineHeight: 1.85,
  mb: 2,
  "& strong": { color: colors.text.primary, fontWeight: 600 },
  "& a": { color: colors.primary.main, textDecoration: "none", "&:hover": { textDecoration: "underline" } },
};

const sidebarLinkSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "10px",
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  display: "flex",
  alignItems: "center",
  gap: 1,
  p: "10px 14px",
  textDecoration: "none",
  border: `1px solid ${colors.border.subtle}`,
  color: colors.text.muted,
  transition: "color 0.2s, border-color 0.2s",
  "&:hover": { color: colors.text.secondary, borderColor: colors.border.default },
};

function DocList({ items, keyWidth = 150 }) {
  return (
    <Box sx={{ my: 2.5 }}>
      {items.map((item) => (
        <Box
          key={item.key}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: `${keyWidth}px 1fr` },
            gap: { xs: 0.5, sm: 2.5 },
            py: 2,
            borderBottom: `1px solid ${colors.border.subtle}`,
            "&:first-of-type": { borderTop: `1px solid ${colors.border.subtle}` },
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: colors.text.primary,
              pt: { sm: "1px" },
            }}
          >
            {item.key}
          </Typography>
          <Typography component="div" sx={{ ...bodySx, mb: 0 }}>
            {item.val}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function Callout({ children }) {
  return (
    <Box
      sx={{
        my: 3,
        p: "18px 22px",
        background: "linear-gradient(180deg, rgba(232,163,61,0.04), transparent)",
        border: "1px solid rgba(232,163,61,0.25)",
        borderLeft: `2px solid ${colors.primary.main}`,
        "& p": { fontSize: "13px", color: colors.text.secondary, lineHeight: 1.7, m: 0 },
        "& strong": { color: colors.primary.main, fontWeight: 600 },
      }}
    >
      {children}
    </Box>
  );
}

function CalloutRed({ children }) {
  return (
    <Box
      sx={{
        my: 3,
        p: "18px 22px",
        background: "linear-gradient(180deg, rgba(220,38,38,0.04), transparent)",
        border: "1px solid rgba(220,38,38,0.25)",
        borderLeft: `2px solid ${DANGER}`,
        "& p": { fontSize: "13px", color: colors.text.secondary, lineHeight: 1.7, m: 0 },
        "& strong": { color: DANGER, fontWeight: 600 },
      }}
    >
      {children}
    </Box>
  );
}

function ProhibitList() {
  return (
    <Box sx={{ my: 2 }}>
      {PROHIBITED.map((text) => (
        <Box
          key={text}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.75,
            py: 1.5,
            borderBottom: `1px solid ${colors.border.subtle}`,
            fontSize: "14px",
            color: colors.text.secondary,
            lineHeight: 1.65,
            "&:first-of-type": { borderTop: `1px solid ${colors.border.subtle}` },
          }}
        >
          <Box
            sx={{
              width: 18,
              height: 18,
              flexShrink: 0,
              mt: "1px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid rgba(220,38,38,0.3)",
            }}
          >
            <Box
              component="svg"
              viewBox="0 0 8 8"
              fill="none"
              stroke={DANGER}
              strokeWidth={2}
              sx={{ width: 8, height: 8 }}
            >
              <line x1="1.5" y1="1.5" x2="6.5" y2="6.5" />
              <line x1="6.5" y1="1.5" x2="1.5" y2="6.5" />
            </Box>
          </Box>
          {text}
        </Box>
      ))}
    </Box>
  );
}

function StrikeCards() {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
        gap: "2px",
        my: 2.5,
      }}
    >
      {STRIKES.map((strike) => (
        <Box
          key={strike.label}
          sx={{
            position: "relative",
            overflow: "hidden",
            background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
            border: `1px solid ${colors.border.subtle}`,
            p: "20px",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "2px",
              bgcolor: strike.accent,
            },
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: "9px",
              fontWeight: 700,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: strike.labelColor,
              mb: 1.25,
            }}
          >
            {strike.label}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: "16px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              color: colors.text.primary,
              mb: 0.75,
            }}
          >
            {strike.title}
          </Typography>
          <Typography sx={{ fontSize: "12px", color: colors.text.secondary, lineHeight: 1.6 }}>
            {strike.body}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function DocSection({ id, num, title, children }) {
  return (
    <Box
      component="section"
      id={id}
      sx={{
        pt: "52px",
        borderTop: `1px solid ${colors.border.subtle}`,
        scrollMarginTop: { xs: `${DARK_HERO_HEADER_HEIGHT_PX + 16}px`, xl: `${DARK_HERO_HEADER_XL_HEIGHT_PX + 16}px` },
      }}
    >
      <Typography sx={sectionLabelSx}>Section {num}</Typography>
      <Typography component="h2" sx={sectionTitleSx}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function DiscordIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  );
}

const contactIconSx = {
  width: 32,
  height: 32,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: colors.background.surface,
  border: `1px solid ${colors.border.subtle}`,
  color: colors.primary.main,
};

export default function TermsOfServicePage() {
  const [activeSection, setActiveSection] = React.useState("s1");

  React.useEffect(() => {
    const sections = TOC_ITEMS.map((item) => document.getElementById(item.id)).filter(Boolean);
    if (!sections.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: "-15% 0px -75% 0px" }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const handleTocClick = (event, id) => {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveSection(id);
  };

  return (
    <Box sx={{ backgroundColor: PAGE_BG, color: colors.text.primary, overflowX: "hidden" }}>
      <PageContainer sx={{ maxWidth: "1140px", px: { xs: 0, md: 0 } }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "flex-start",
            minHeight: "100vh",
          }}
        >
          <Box
            component="aside"
            sx={{
              width: { xs: "100%", md: "240px" },
              flexShrink: 0,
              position: { md: "sticky" },
              top: { md: `${DARK_HERO_HEADER_HEIGHT_PX}px`, xl: `${DARK_HERO_HEADER_XL_HEIGHT_PX}px` },
              alignSelf: "flex-start",
              maxHeight: {
                md: `calc(100vh - ${DARK_HERO_HEADER_HEIGHT_PX}px)`,
                xl: `calc(100vh - ${DARK_HERO_HEADER_XL_HEIGHT_PX}px)`,
              },
              overflowY: { md: "auto" },
              py: { xs: 3, md: 7 },
              px: { xs: 3, md: "40px 40px 56px 48px" },
              borderRight: { md: `1px solid ${colors.border.subtle}` },
              borderBottom: { xs: `1px solid ${colors.border.subtle}`, md: "none" },
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Typography
              sx={{
                fontFamily: "'Inter', system-ui, sans-serif",
                fontSize: "9px",
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: colors.text.muted,
                mb: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
                "&::before": { content: '""', width: 12, height: "1px", bgcolor: colors.text.muted, display: "block" },
              }}
            >
              Contents
            </Typography>

            <Box component="nav" sx={{ display: "flex", flexDirection: "column" }}>
              {TOC_ITEMS.map((item) => {
                const active = activeSection === item.id;
                return (
                  <Box
                    key={item.id}
                    component="a"
                    href={`#${item.id}`}
                    onClick={(event) => handleTocClick(event, item.id)}
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 1.25,
                      py: 1.125,
                      borderBottom: "1px solid rgba(255,255,255,0.03)",
                      textDecoration: "none",
                      fontSize: "12px",
                      lineHeight: 1.4,
                      color: active ? colors.primary.main : colors.text.muted,
                      transition: "color 0.15s",
                      "&:hover": { color: active ? colors.primary.main : colors.text.secondary },
                      "&:last-of-type": { borderBottom: "none" },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontFamily: "'Inter', system-ui, sans-serif",
                        fontSize: "10px",
                        fontWeight: 600,
                        letterSpacing: "0.1em",
                        flexShrink: 0,
                        width: 20,
                        color: active ? colors.primary.main : colors.text.muted,
                      }}
                    >
                      {item.num}
                    </Box>
                    {item.label}
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ height: "1px", bgcolor: colors.border.subtle, my: 2.5 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
              {[
                { label: "Last updated", val: LAST_UPDATED },
                { label: "Governing law", val: "Germany" },
                { label: "Min. age", val: "13 years" },
              ].map((row) => (
                <Box key={row.label}>
                  <Typography
                    sx={{
                      fontFamily: "'Inter', system-ui, sans-serif",
                      fontSize: "9px",
                      fontWeight: 700,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      color: colors.text.muted,
                    }}
                  >
                    {row.label}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: colors.text.secondary }}>{row.val}</Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 1 }}>
              <Box component={Link} to="/privacy" sx={sidebarLinkSx}>
                Privacy Policy →
              </Box>
              <Box component={Link} to="/data-protection-policy-and-gdpr" sx={sidebarLinkSx}>
                GDPR Policy →
              </Box>
            </Box>
          </Box>

          <Box
            component="article"
            sx={{
              flex: 1,
              minWidth: 0,
              maxWidth: "720px",
              py: { xs: 5, md: 7 },
              px: { xs: 3, md: "64px 48px 120px" },
              pb: { xs: 10, md: 15 },
            }}
          >
            <Box component="header" sx={{ mb: 7, pb: 5, borderBottom: `1px solid ${colors.border.subtle}` }}>
              <Typography
                sx={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  color: colors.primary.main,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  mb: 2.5,
                  "&::before": { content: '""', width: 20, height: "1px", bgcolor: colors.primary.main, display: "block" },
                }}
              >
                Legal document
              </Typography>
              <Typography
                component="h1"
                sx={{
                  fontFamily: "'Inter', system-ui, sans-serif",
                  fontSize: { xs: "2.5rem", sm: "3.25rem", md: "4.25rem" },
                  fontWeight: 700,
                  textTransform: "uppercase",
                  lineHeight: 0.92,
                  letterSpacing: "-0.01em",
                  color: colors.text.primary,
                  mb: 3,
                }}
              >
                Terms
                <br />
                of Use
              </Typography>
              <Typography sx={{ fontSize: "16px", fontWeight: 300, color: colors.text.secondary, lineHeight: 1.8, mb: 3.5 }}>
                Welcome to Ouro. By accessing or using the platform, you agree to be bound by these terms.
                Please read them carefully — they govern your use of everything Ouro provides.
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, pt: 2.5, borderTop: `1px solid ${colors.border.subtle}` }}>
                {[
                  { label: "Last updated", val: LAST_UPDATED },
                  { label: "Applies to", val: "ouro.is and all Ouro services" },
                  {
                    label: "Questions",
                    val: (
                      <Box component="a" href={`mailto:${SUPPORT_EMAIL}`} sx={{ color: colors.primary.main, textDecoration: "none" }}>
                        {SUPPORT_EMAIL}
                      </Box>
                    ),
                  },
                ].map((item) => (
                  <Box key={item.label}>
                    <Typography
                      sx={{
                        fontFamily: "'Inter', system-ui, sans-serif",
                        fontSize: "9px",
                        fontWeight: 700,
                        letterSpacing: "0.18em",
                        textTransform: "uppercase",
                        color: colors.text.muted,
                        mb: 0.375,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography sx={{ fontSize: "13px", color: colors.text.secondary }}>{item.val}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <DocSection id="s1" num="01" title="Acceptance of terms">
              <Typography sx={bodySx}>
                By creating an account or using Ouro&apos;s services, you acknowledge that you have read,
                understood, and agree to be bound by these Terms of Use and our{" "}
                <Box component={Link} to="/privacy">Privacy Policy</Box>.
              </Typography>
              <Typography sx={{ ...bodySx, mb: 0 }}>
                If you do not agree with any part of these terms, you should not use Ouro.
              </Typography>
            </DocSection>

            <DocSection id="s2" num="02" title="Eligibility">
              <Typography sx={bodySx}>
                To use Ouro, you must be at least <strong>13 years of age</strong>. By using our service, you
                represent and warrant that you meet this age requirement and that you have the legal capacity to
                enter into this agreement.
              </Typography>
              <Typography sx={{ ...bodySx, mb: 0 }}>
                If you are between 13 and 18 years of age, you confirm that you have obtained the consent of a
                parent or legal guardian to use the service.
              </Typography>
            </DocSection>

            <DocSection id="s3" num="03" title="Account registration">
              <Typography sx={bodySx}>
                Creating an account on Ouro involves a multi-step process designed to maintain the integrity of
                the platform. The following conditions apply.
              </Typography>
              <DocList
                keyWidth={160}
                items={[
                  {
                    key: "Sign-in method",
                    val: "Account creation currently requires a Google account (Gmail) or Steam sign-in via official OAuth. You agree to provide a valid account during registration. Steam access is read-only — Ouro never accesses your inventory, trades, or market listings.",
                  },
                  {
                    key: "Verification",
                    val: "Registering an account does not automatically grant access to matchmaking. All accounts are subject to human verification to ensure they meet our requirements. This process maintains the integrity and fairness of the platform.",
                  },
                  {
                    key: "Accuracy",
                    val: "You agree to provide accurate, current, and complete information when registering. You are responsible for maintaining the accuracy of this information throughout your use of Ouro.",
                  },
                  {
                    key: "Security",
                    val: (
                      <>
                        You are responsible for safeguarding your account credentials. You agree not to share
                        your account details with others and to notify us immediately of any unauthorised use of
                        your account at{" "}
                        <Box component="a" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Box>.
                      </>
                    ),
                  },
                  {
                    key: "Account activation",
                    val: "ID verification is optional. It runs through didit.me, adds to your reputation, and is not required to activate your account or to play. Every account still goes through human review, which is what keeps the environment safe and fair.",
                  },
                  {
                    key: "Termination",
                    val: "We reserve the right to suspend or terminate your account at our discretion if we believe you have violated these Terms or engaged in activities that harm the Ouro community.",
                  },
                ]}
              />
            </DocSection>

            <DocSection id="s4" num="04" title="Use of the service">
              <Typography sx={bodySx}>
                Ouro is intended for your personal, non-commercial use. You agree not to use the service for any
                unlawful or unauthorised purposes.
              </Typography>
              <Typography sx={bodySx}>The following activities are strictly prohibited on Ouro:</Typography>
              <ProhibitList />
              <Typography sx={{ ...bodySx, mb: 0 }}>
                Ouro is a community-driven platform. You agree to treat others with respect and adhere to our
                community guidelines, which prohibit offensive, defamatory, or otherwise inappropriate behaviour.
              </Typography>
            </DocSection>

            <DocSection id="s5" num="05" title="Violations & disciplinary actions">
              <Typography sx={bodySx}>
                Violations of these Terms are handled through a graduated disciplinary process. The severity of
                the action taken depends on the nature and frequency of the violation.
              </Typography>
              <StrikeCards />
              <CalloutRed>
                <Typography component="p">
                  <strong>Note:</strong> For severe violations — including confirmed cheating, harassment, or
                  identity fraud — Ouro reserves the right to permanently ban an account immediately, without
                  progressing through the three-strike sequence.
                </Typography>
              </CalloutRed>
            </DocSection>

            <DocSection id="s6" num="06" title="Intellectual property">
              <Typography sx={bodySx}>
                All content, features, and functionality on Ouro — including but not limited to software, text,
                images, and logos — are the intellectual property of Ouro or its licensors and are protected by
                applicable copyright, trademark, and intellectual property laws.
              </Typography>
              <DocList
                keyWidth={160}
                items={[
                  {
                    key: "Ownership",
                    val: "Ouro and its licensors retain all rights, title, and interest in and to the service and its content. Nothing in these Terms transfers any intellectual property rights to you.",
                  },
                  {
                    key: "License to you",
                    val: "We grant you a limited, non-exclusive, non-transferable, revocable licence to use the service for personal, non-commercial purposes in accordance with these Terms.",
                  },
                ]}
              />
            </DocSection>

            <DocSection id="s7" num="07" title="User-generated content">
              <Typography sx={bodySx}>
                You are solely responsible for any content you create, post, or share on Ouro — including chat
                messages, reports, and feedback.
              </Typography>
              <DocList
                keyWidth={160}
                items={[
                  {
                    key: "Responsibility",
                    val: "You confirm that any content you submit does not violate these Terms, applicable law, or the rights of any third party.",
                  },
                  {
                    key: "Licence to Ouro",
                    val: "By submitting content, you grant Ouro a worldwide, non-exclusive, royalty-free, perpetual licence to use, modify, reproduce, and distribute your content solely in connection with the operation of the service.",
                  },
                  {
                    key: "Removal",
                    val: "We reserve the right to remove any content that violates these Terms or our community guidelines, without prior notice.",
                  },
                ]}
              />
            </DocSection>

            <DocSection id="s8" num="08" title="Payment & refunds">
              <DocList
                keyWidth={160}
                items={[
                  {
                    key: "Payments",
                    val: "Some features of Ouro require payment. You agree to provide accurate payment information and authorise us to charge the applicable fees for the membership tier you select.",
                  },
                  {
                    key: "Refunds",
                    val: "All payments made through Ouro are non-refundable, except as required by applicable law or as otherwise stated in specific terms for a particular service or promotional offer.",
                  },
                ]}
              />
              <Callout>
                <Typography component="p">
                  If you believe a charge was made in error, contact us at{" "}
                  <strong>{SUPPORT_EMAIL}</strong> within 14 days of the transaction. We will review and respond
                  within 7 business days.
                </Typography>
              </Callout>
            </DocSection>

            <DocSection id="s9" num="09" title="Disclaimers">
              <DocList
                keyWidth={160}
                items={[
                  {
                    key: "No warranty",
                    val: 'Ouro is provided on an "as is" and "as available" basis. We make no warranties or representations — express or implied — about the accuracy, reliability, availability, or fitness for a particular purpose of the service.',
                  },
                  {
                    key: "Limitation of liability",
                    val: "To the fullest extent permitted by law, Ouro and its affiliates shall not be liable for any indirect, incidental, consequential, special, or punitive damages arising from your use of — or inability to use — the service.",
                  },
                ]}
              />
            </DocSection>

            <DocSection id="s10" num="10" title="Changes to the service">
              <Typography sx={bodySx}>
                We may modify, suspend, or discontinue any part of Ouro at any time, with or without notice. We
                are not liable to you or any third party for any such changes, suspensions, or discontinuations.
              </Typography>
              <Typography sx={{ ...bodySx, mb: 0 }}>
                We will try to provide reasonable advance notice for significant changes that affect core
                platform functionality, but we cannot guarantee this in all cases.
              </Typography>
            </DocSection>

            <DocSection id="s11" num="11" title="Changes to these terms">
              <Typography sx={bodySx}>
                We may update these Terms of Use from time to time to reflect changes in our practices or legal
                obligations. We will notify you of any significant changes by posting the updated terms on this
                page and updating the &quot;Last Updated&quot; date above.
              </Typography>
              <Typography sx={{ ...bodySx, mb: 0 }}>
                For material changes that affect your rights, we will send a notification to the email address
                associated with your account. Continued use of Ouro after the updated terms take effect
                constitutes your acceptance of the revised Terms.
              </Typography>
            </DocSection>

            <DocSection id="s12" num="12" title="Governing law">
              <Typography sx={bodySx}>
                These Terms of Use are governed by and construed in accordance with the laws of{" "}
                <strong>Germany</strong>, without regard to its conflict of law principles.
              </Typography>
              <Typography sx={bodySx}>
                You agree to submit to the exclusive jurisdiction of the courts located in Germany for the
                resolution of any disputes arising out of or relating to these terms or your use of Ouro.
              </Typography>
              <Callout>
                <Typography component="p">
                  If you are a consumer located in the European Union, you may also be entitled to raise a
                  dispute through the EU Online Dispute Resolution platform at{" "}
                  <strong>ec.europa.eu/odr</strong>.
                </Typography>
              </Callout>
            </DocSection>

            <DocSection id="s13" num="13" title="Contact us">
              <Typography sx={bodySx}>
                If you have any questions about these Terms of Use or our practices, reach out directly. We
                don&apos;t route legal inquiries through a chatbot.
              </Typography>
              <Box
                sx={{
                  mt: 2.5,
                  background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
                  border: `1px solid ${colors.border.subtle}`,
                  p: "24px 28px",
                }}
              >
                {[
                  {
                    label: "Email",
                    icon: <MailOutlineRounded sx={{ fontSize: 14 }} />,
                    val: (
                      <Box component="a" href={`mailto:${SUPPORT_EMAIL}`} sx={{ color: colors.primary.main, textDecoration: "none" }}>
                        {SUPPORT_EMAIL}
                      </Box>
                    ),
                  },
                  {
                    label: "Support ticket",
                    icon: <ConfirmationNumberOutlined sx={{ fontSize: 14 }} />,
                    val: (
                      <Box component={Link} to="/support" sx={{ color: colors.primary.main, textDecoration: "none" }}>
                        ouro.is/support
                      </Box>
                    ),
                  },
                  {
                    label: "Community",
                    icon: <DiscordIcon />,
                    val: (
                      <Box
                        component="a"
                        href={DISCORD_INVITE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: colors.primary.main, textDecoration: "none" }}
                      >
                        Discord server
                      </Box>
                    ),
                  },
                ].map((row, index, arr) => (
                  <Box
                    key={row.label}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      py: 1.75,
                      borderBottom: index < arr.length - 1 ? `1px solid ${colors.border.subtle}` : "none",
                    }}
                  >
                    <Box sx={contactIconSx}>{row.icon}</Box>
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: "'Inter', system-ui, sans-serif",
                          fontSize: "10px",
                          fontWeight: 700,
                          letterSpacing: "0.15em",
                          textTransform: "uppercase",
                          color: colors.text.muted,
                          mb: 0.25,
                        }}
                      >
                        {row.label}
                      </Typography>
                      <Typography sx={{ fontSize: "13px", color: colors.text.secondary }}>{row.val}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </DocSection>
          </Box>
        </Box>
      </PageContainer>
    </Box>
  );
}
